import { FieldPath, useFieldArray } from 'react-hook-form';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { AlertNotification } from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { Switch } from '@givebutter/primitives/switch';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GenerateExportsHistoryParams, generateExports } from 'api/contacts/exports';
import { fetchReportColumns } from 'api/reportFields';
import * as Dialog from 'components/dialog';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { usePaginatedCount } from 'customHooks/usePaginatedCount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ExportTransactionsFormValues {
    type: string;
    isGroup: boolean;
    headings: { name: string; label: string; checked: boolean }[];
    headingsType: 'all' | 'custom';
}

interface ExportTransactionsDialogProps extends Dialog.DialogProps {
    autoCloseOnSuccess?: boolean;
    onExportSuccess?: () => void;
}

const ExportTransactionsDialog = (props: ExportTransactionsDialogProps) => {
    const toast = useToast();
    const { children, autoCloseOnSuccess = true, onExportSuccess, ...rest } = props;
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const { filters } = useFiltersByKey('transactions');
    const [transactionsCount] = usePaginatedCount(
        transactionQueryKeys.transactionsWithFilters(accountId),
    );
    const reportType = 'transactions';

    const { data: columns = [] } = useSuspenseQuery({
        queryKey: accountQueryKeys.reportColumnsByType(accountId, reportType),
        queryFn: () => fetchReportColumns(accountId, { reportType }),
        select: data => {
            if (data) {
                return Object.entries(data).map(
                    (
                        column,
                    ): {
                        name: string;
                        label: string;
                    } => {
                        return {
                            name: column[0].toString(),
                            label: column[1].toString(),
                        };
                    },
                );
            }
        },
        staleTime: Infinity,
    });

    const methods = Form.useGivebutterForm<ExportTransactionsFormValues>({
        defaultValues: {
            type: 'all',
            headingsType: 'all',
            isGroup: false,
            headings: columns.map(col => {
                return { name: col.name, label: col.label, checked: true };
            }),
        },
    });

    const {
        control,
        handleSubmit,
        setError,
        clearErrors,
        getValues,
        setValue,
        watch,
        formState: { errors },
    } = methods;
    const [headings, headingsType] = watch(['headings', 'headingsType']);
    const headingsFieldArray = useFieldArray({ name: 'headings', control });
    const { fields: headingsFields } = headingsFieldArray;
    const selectedHeadings = headings.filter(item => item.checked);
    const apiError = (errors as any)['api-error']?.message;

    const { mutate: exportTransactions, isPending: isLoading } = useMutation({
        mutationFn: (formParams: GenerateExportsHistoryParams) =>
            generateExports(accountId, {
                scope: 'all',
                object: 'account',
                report_type: 'csv',
                resource: 'transactions',
                object_id: accountId,
                filters,
                ...formParams,
            }),
        onSuccess: () => {
            toast.success(`Success! We'll send you an email when your export is ready.`);
            if (autoCloseOnSuccess) {
                forceRender();
            }

            onExportSuccess?.();
        },
        onError: (error: GivebutterAxiosError) => {
            setError('api-error' as FieldPath<ExportTransactionsFormValues>, {
                type: 'custom',
                message:
                    error.response?.data.message ?? 'Unable to generate report. Please try again.',
            });
        },
    });

    const submit = (values: ExportTransactionsFormValues) => {
        exportTransactions({
            type: values.type === 'all' ? '' : 'items',
            group: values.isGroup,
            headings:
                selectedHeadings.length === columns.length
                    ? []
                    : selectedHeadings.map(item => item.name),
        });
    };

    const selectAllColumns = () =>
        setValue(
            'headings',
            getValues('headings').map(h => {
                return { ...h, checked: true };
            }),
        );
    const deselectAllColumns = () =>
        setValue(
            'headings',
            getValues('headings').map(h => {
                return { ...h, checked: false };
            }),
        );

    return (
        <Form.GivebutterFormProvider {...methods}>
            <Dialog.Root key={key} {...rest}>
                {children}
                <Dialog.Portal>
                    <Dialog.Overlay />
                    <Dialog.Content className="max-w-[40rem]">
                        <Dialog.Body>
                            <Dialog.Header className="!items-start !px-0 !pb-8">
                                <div
                                    className={
                                        'flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 text-gray-600'
                                    }
                                >
                                    <FontAwesomeIcon icon={faDownload} className={'h-5 w-5'} />
                                </div>
                                <Dialog.Title className="text-xl">Export transactions</Dialog.Title>
                                <p className="text-base">
                                    Select the transaction information that you'd like to export.
                                    Once your export is ready, we'll send you a secure download link
                                    via email.
                                </p>
                            </Dialog.Header>
                            <Dialog.Description className="w-full">
                                <div className={'flex flex-col gap-y-4'}>
                                    <div className={'flex flex-col gap-2'}>
                                        <p className={'text-sm font-medium'}>Exporting</p>
                                        <p className={'font-medium leading-none'}>
                                            Transactions ({transactionsCount}{' '}
                                            {pluralize(transactionsCount, 'row')})
                                        </p>
                                    </div>
                                    <form
                                        className={'flex flex-col gap-y-4'}
                                        onSubmit={handleSubmit(submit)}
                                    >
                                        <Form.Container>
                                            <Form.Label name="type">Transactions</Form.Label>
                                            <Form.SingleSelect control={control} name="type">
                                                <SingleSelect.Trigger aria-label="Transaction type" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="all">
                                                        <SingleSelect.ItemText>
                                                            All transactions
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="all-with-item">
                                                        <SingleSelect.ItemText>
                                                            All transactions with item breakdown
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>

                                        <Form.Container>
                                            <Form.Label
                                                name="isGroup"
                                                className={
                                                    'flex w-fit cursor-pointer items-center gap-2'
                                                }
                                            >
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={'isGroup'}
                                                />
                                                Group transactions by campaign
                                            </Form.Label>
                                        </Form.Container>

                                        <Form.Container>
                                            <Form.UseFormLabel name="headingsType">
                                                Columns
                                            </Form.UseFormLabel>
                                            <Form.SingleSelect
                                                control={control}
                                                name="headingsType"
                                                onValueChange={value => {
                                                    if (value === 'all') {
                                                        selectAllColumns();
                                                    }
                                                }}
                                            >
                                                <SingleSelect.Trigger aria-label="Columns type" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="all">
                                                        <SingleSelect.ItemText>
                                                            All columns
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item
                                                        value="custom"
                                                        disabled={columns.length === 0}
                                                    >
                                                        <SingleSelect.ItemText>
                                                            Custom
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>

                                        {headingsType === 'custom' && (
                                            <>
                                                <div className="flex gap-3">
                                                    <Label htmlFor="selectAllColumns">
                                                        Select all
                                                    </Label>
                                                    <Switch
                                                        id="selectAllColumns"
                                                        checked={
                                                            selectedHeadings.length ===
                                                            columns.length
                                                        }
                                                        onCheckedChange={checked => {
                                                            if (checked) {
                                                                selectAllColumns();
                                                            } else {
                                                                deselectAllColumns();
                                                            }
                                                        }}
                                                    />
                                                </div>

                                                <div
                                                    className={
                                                        'grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3'
                                                    }
                                                >
                                                    {headingsFields.map((field, index) => {
                                                        return (
                                                            <div
                                                                key={field.id}
                                                                className={
                                                                    'flex cursor-pointer flex-wrap gap-2'
                                                                }
                                                            >
                                                                <Form.UseFormCheckboxInput
                                                                    id={field.name}
                                                                    value={field.name}
                                                                    control={control}
                                                                    name={`headings.${index}.checked`}
                                                                    checked={field.checked}
                                                                />

                                                                <Form.UseFormLabel
                                                                    name={`headings.${index}.checked`}
                                                                    className="max-w-[80%] break-words"
                                                                >
                                                                    {field.label}
                                                                </Form.UseFormLabel>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}

                                        {apiError && (
                                            <AlertNotification
                                                closable={true}
                                                type={'error'}
                                                onClose={() =>
                                                    clearErrors(
                                                        'api-error' as FieldPath<ExportTransactionsFormValues>,
                                                    )
                                                }
                                            >
                                                {apiError}
                                            </AlertNotification>
                                        )}
                                        <div className="flex justify-end gap-3">
                                            <Dialog.Cancel asChild>
                                                <Button variant="outline" color="neutral">
                                                    Cancel
                                                </Button>
                                            </Dialog.Cancel>
                                            <Form.UseFormSubmitButton
                                                onClick={() => clearErrors()}
                                                disabled={
                                                    isLoading ||
                                                    (selectedHeadings.length === 0 &&
                                                        headingsType !== 'all')
                                                }
                                            >
                                                {isLoading ? (
                                                    <FontAwesomeIcon
                                                        className={'animate-spin'}
                                                        icon={faSpinnerThird}
                                                    />
                                                ) : (
                                                    'Export'
                                                )}
                                            </Form.UseFormSubmitButton>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Description>
                        </Dialog.Body>
                        <Dialog.Close />
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </Form.GivebutterFormProvider>
    );
};

const ExportTransactionsModalTrigger = Dialog.Trigger;

const Root = ExportTransactionsDialog;
const Trigger = ExportTransactionsModalTrigger;

export {
    ExportTransactionsDialog,
    ExportTransactionsModalTrigger,
    //
    Root,
    Trigger,
};
